import { useEffect, useRef, useState } from "react";
import { captureError } from "@/lib/sentry";
import { Link } from "react-router-dom";
import AppLayout from "@/components/AppLayout";
import { api } from "@/lib/api";
import { LANGUAGES } from "@/lib/constants";
import { useAuth } from "@/context/AuthContext";
import {
  MicrophoneStageIcon,
  ArrowLeftIcon,
  LockKeyIcon,
  PaperPlaneRightIcon,
  UserIcon,
  ArrowClockwiseIcon,
} from "@phosphor-icons/react";

const QUICK_QUESTIONS = [
  "Walk me through the turning point tonight.",
  "What was working for you out there?",
  "How do you respond to the critics?",
  "What's the message to the locker room?",
  "What do you need to clean up next game?",
];

export default function PressBoxPressConference() {
  const { user } = useAuth();
  const isLocked = user?.plan !== "press_box_pro";

  const [games, setGames] = useState([]);
  const [gameId, setGameId] = useState("");
  const [subject, setSubject] = useState("");
  const [role, setRole] = useState("player");
  const [language, setLanguage] = useState(user?.language_preference || "en");
  const [started, setStarted] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]); // {id, who:'reporter'|'subject', text}
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);
  const msgIdRef = useRef(0);
  const nextMsgId = () => ++msgIdRef.current;

  useEffect(() => {
    api.get("/games").then((r) => setGames(r.data || [])).catch((e) => captureError("pc games load", e));
  }, []);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, loading]);

  const startConference = () => {
    if (!subject.trim() || isLocked) return;
    setSessionId((typeof crypto !== "undefined" && crypto.randomUUID)
      ? crypto.randomUUID()
      : `pc_${Date.now()}_${Math.random().toString(36).slice(2)}`);
    setMessages([]);
    setStarted(true);
  };

  const reset = () => {
    setStarted(false);
    setMessages([]);
    setSessionId(null);
    setInput("");
  };

  const ask = async (preset) => {
    const text = (preset ?? input).trim();
    if (!text || loading || isLocked) return;
    setInput("");
    setMessages((m) => [...m, { id: nextMsgId(), who: "reporter", text }]);
    setLoading(true);
    try {
      const res = await api.post("/press-box/press-conference", {
        subject,
        role,
        message: text,
        game_id: gameId || null,
        session_id: sessionId,
        language,
      });
      setMessages((m) => [...m, { id: nextMsgId(), who: "subject", text: res.data.text }]);
    } catch (err) {
      captureError("press conference failed:", err);
      setMessages((m) => [...m, { id: nextMsgId(), who: "subject", text: "(No comment — the mic cut out. Try again.)" }]);
    } finally {
      setLoading(false);
    }
  };

  if (isLocked) {
    return (
      <AppLayout>
        <div className="max-w-xl mx-auto text-center py-20" data-testid="pc-locked">
          <LockKeyIcon weight="fill" className="text-[#FDB927] mx-auto mb-3" size={36} />
          <h1 className="font-display font-bold text-3xl uppercase tracking-tighter mb-3">Press Box Pro feature</h1>
          <Link to="/pricing?plan=press_box_pro" data-testid="press-conference-locked-cta" className="inline-flex items-center gap-1 px-5 py-2 rounded-md bg-[#FDB927] hover:bg-yellow-400 text-black text-xs font-bold uppercase tracking-wider">Upgrade</Link>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <Link to="/press-box" className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wider text-white/60 hover:text-white mb-4" data-testid="back-to-press-box">
        <ArrowLeftIcon size={14} /> Back to Press Box
      </Link>
      <div className="mb-6 fade-up">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#FDB927] mb-2 inline-flex items-center gap-1.5">
          <MicrophoneStageIcon weight="fill" /> AI Press Conference
        </p>
        <h1 className="font-display font-bold text-4xl md:text-5xl uppercase tracking-tighter">Interview the Room</h1>
        <p className="text-white/60 text-sm mt-2 max-w-2xl">
          Run a live, multi-turn Q&amp;A with a player or coach. Answers are an <span className="text-[#FDB927] font-semibold">AI simulation</span> in that person's on-air voice — grounded in real game facts, never real quotes.
        </p>
      </div>

      {!started ? (
        <div className="max-w-xl p-5 rounded-md bg-[#121212] border border-white/10 space-y-3" data-testid="pc-setup">
          <div>
            <label className="text-[10px] font-bold uppercase tracking-[0.18em] text-white/40 mb-2 block">Subject (player or coach)</label>
            <input
              data-testid="pc-subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="e.g. Aaron Judge, Erik Spoelstra"
              className="w-full bg-black border border-white/15 rounded-md px-3 py-2 text-sm"
            />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] font-bold uppercase tracking-[0.18em] text-white/40 mb-2 block">Role</label>
              <select value={role} onChange={(e) => setRole(e.target.value)} data-testid="pc-role" className="w-full bg-black border border-white/15 rounded-md px-3 py-2 text-sm uppercase font-bold tracking-wider">
                <option value="player">Player</option>
                <option value="coach">Coach</option>
              </select>
            </div>
            <div>
              <label className="text-[10px] font-bold uppercase tracking-[0.18em] text-white/40 mb-2 block">Language</label>
              <select value={language} onChange={(e) => setLanguage(e.target.value)} data-testid="pc-lang" className="w-full bg-black border border-white/15 rounded-md px-3 py-2 text-sm uppercase font-bold tracking-wider">
                {LANGUAGES.map((l) => <option key={l.value} value={l.value}>{l.label}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase tracking-[0.18em] text-white/40 mb-2 block">Game context (optional — grounds answers in real facts)</label>
            <select value={gameId} onChange={(e) => setGameId(e.target.value)} data-testid="pc-game" className="w-full bg-black border border-white/15 rounded-md px-3 py-2 text-sm">
              <option value="">No specific game</option>
              {games.map((g) => (
                <option key={g.id} value={g.id}>{g.league} · {g.away_team_abbr} @ {g.home_team_abbr} · {g.period_label}</option>
              ))}
            </select>
          </div>
          <button
            onClick={startConference}
            disabled={!subject.trim()}
            data-testid="pc-start"
            className="w-full py-3 rounded-md bg-[#FDB927] hover:bg-yellow-400 disabled:opacity-40 text-black font-black uppercase tracking-wide inline-flex items-center justify-center gap-2"
          >
            <MicrophoneStageIcon weight="fill" /> Start the press conference
          </button>
        </div>
      ) : (
        <div className="max-w-3xl" data-testid="pc-room">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2 min-w-0">
              <div className="w-9 h-9 rounded-full bg-[#FDB927]/15 border border-[#FDB927]/40 flex items-center justify-center shrink-0">
                <UserIcon weight="fill" size={18} className="text-[#FDB927]" />
              </div>
              <div className="min-w-0">
                <p className="font-display font-bold text-lg uppercase tracking-tight truncate" data-testid="pc-active-subject">{subject}</p>
                <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-white/40">{role} · AI-simulated</p>
              </div>
            </div>
            <button onClick={reset} data-testid="pc-reset" className="inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider text-white/50 hover:text-white">
              <ArrowClockwiseIcon size={13} /> New
            </button>
          </div>

          <div ref={scrollRef} className="rounded-md bg-[#0d0d0d] border border-white/10 p-4 space-y-3 h-[46vh] overflow-y-auto" data-testid="pc-transcript">
            {messages.length === 0 && (
              <p className="text-white/40 text-sm text-center py-8">Ask your first question — or tap one below.</p>
            )}
            {messages.map((m, i) => (
              <div key={m.id} className={`flex ${m.who === "reporter" ? "justify-end" : "justify-start"}`} data-testid={`pc-msg-${m.who}-${i}`}>
                <div className={`max-w-[85%] px-3 py-2 rounded-md text-sm leading-relaxed ${
                  m.who === "reporter"
                    ? "bg-[#0A84FF]/15 border border-[#0A84FF]/30 text-white"
                    : "bg-[#121212] border border-[#FDB927]/25 text-white/90"
                }`}>
                  <p className="text-[9px] font-bold uppercase tracking-[0.18em] mb-1 opacity-60">{m.who === "reporter" ? "Reporter" : subject}</p>
                  <pre className="whitespace-pre-wrap font-sans">{m.text}</pre>
                </div>
              </div>
            ))}
            {loading && <p className="text-white/50 text-xs font-display tracking-wider" data-testid="pc-thinking">{subject} is stepping to the mic…</p>}
          </div>

          <div className="mt-3 flex gap-1.5 overflow-x-auto pb-1" data-testid="pc-quick">
            {QUICK_QUESTIONS.map((q, i) => (
              <button key={q} onClick={() => ask(q)} disabled={loading} data-testid={`pc-quick-${i}`} className="shrink-0 px-3 py-1.5 rounded-full bg-white/5 border border-white/15 text-[11px] text-white/70 hover:text-white hover:border-white/30 whitespace-nowrap">
                {q}
              </button>
            ))}
          </div>

          <form onSubmit={(e) => { e.preventDefault(); ask(); }} className="mt-2 flex gap-2">
            <input
              data-testid="pc-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask your question…"
              className="flex-1 bg-black border border-white/15 rounded-md px-3 py-2.5 text-sm"
            />
            <button type="submit" disabled={loading || !input.trim()} data-testid="pc-send" className="px-4 rounded-md bg-[#FDB927] hover:bg-yellow-400 disabled:opacity-40 text-black inline-flex items-center justify-center">
              <PaperPlaneRightIcon weight="fill" size={18} />
            </button>
          </form>
        </div>
      )}
    </AppLayout>
  );
}
